import './styles/header.scss';
import './images/logo.svg';

import { renderSpotlight } from './components/spotlight';
import { renderNavigation } from './components/navigation';
import { renderLogo } from './components/logo';
import { renderQuickLinks } from './components/quick-links';
import { renderSpinner } from './components/spinner';
import { getTrimBundle } from './i18n';
import { getHomepageContext } from '@amzn/brand-context-service-type-script-client';
import { Metric } from '@amzn/katal-metrics';
import { makeMetricsPublisher, metricsPublisher } from './util/metrics';
import { getExperiment } from './util/experiments';
import { renderSpooferBar } from 'src/components/spoofer-bar';
import { renderError } from 'src/components/error';
import {
  listWebsiteCommunications,
} from '@amzn/brand-registry-communication-service-type-script-client';
import { logError, logger, logWarning } from 'src/util/logger';
import { errorOf, isNetworkError, isUnsupportedBrowserError } from 'src/util/errors';
import { customerIsPzEligible } from './util/context-visibility';

enum InitializationStatus {
  Failure = 0,
  Success = 1,
}

const initializationMetric = new Metric.Initialization().withMonitor();
const networkErrorMetric = new Metric.Counter('NetworkError', 0).withMonitor();
const unsupportedBrowserErrorMetric = new Metric.Counter('UnsupportedBrowserError', 0).withMonitor();

const timerMetric = new Metric.TimerStopwatch('TimeToLoadHeader').withMonitor();
timerMetric.start();

const root = document.getElementById('brand-registry-header-root');
const navRoot = document.getElementById('brand-registry-nav-tabs-root');
const spotlightRoot = document.getElementById('brand-registry-header-spotlight-root');
const logoRoot = document.getElementById('brand-registry-header-logo');
const quickLinksRoot = document.getElementById('brand-registry-header-quick-links-root');
const loadingRoot = document.getElementById('brand-registry-header-spinner-root');
const spooferRoot = document.getElementById('brand-registry-spoofer-root');

if (!loadingRoot || !logoRoot) {
  throw new Error('Loading elements not found');
}

const toggleSpinner = () => {
  const spinner = document.getElementById('brand-registry-header-spinner');
  if (!spinner) {
    // if no spinner already existing, add it to the DOM
    const spinner = renderSpinner();
    loadingRoot.appendChild(spinner);
  } else {
    // if spinner is currently showing, remove it
    loadingRoot.removeChild(spinner);
  }
};

const appendSpotlight = async (theRoot: HTMLElement, pzEligible: boolean) => {

  logger.info('Appending Spotlight...');

  const searchParams = new URLSearchParams(window.location.search);

  // If Cypress is defined, this is a test run and thus we only auto-open the spotlight if this query param is truthy.
  const allowAutoOpen = !window.Cypress || !!searchParams.get('allow-spotlight-auto-open');

  let isLoading = true;
  let hasError = false;

  const loadingSpotlight = renderSpotlight(allowAutoOpen, isLoading, hasError);

  theRoot.append(loadingSpotlight);

  try {
    const communications = (await listWebsiteCommunications('SPOTLIGHT')).filter(c => {
      // this special-case filtering logic was added to prevent turning BR Comms into a rule engine
      // only show the pz-eligibility communication if the customer is PZ eligibile
      // if pzEligible, don't do any filtering
      return pzEligible || (c.communicationId !== 'project-zero-eligibility');
    });

    isLoading = false;

    theRoot.removeChild(loadingSpotlight);

    theRoot.append(renderSpotlight(allowAutoOpen, isLoading, hasError, communications));

    logger.info('Appended Spotlight');
  } catch (error) {
    isLoading = false;
    hasError = true;

    logError('An error occurred retrieving Spotlight communications', error);

    theRoot.removeChild(loadingSpotlight);
    theRoot.append(renderSpotlight(allowAutoOpen, isLoading, hasError));
  }
};

(async() => {
  makeMetricsPublisher('');
  let initializationMetricsPublisher = metricsPublisher.newChildActionPublisherForInitialization();
  let status: InitializationStatus | null = null;

  const initialLogo = renderLogo();
  logoRoot.appendChild(initialLogo);
  toggleSpinner();

  if (!root || !navRoot || !spotlightRoot || !quickLinksRoot || !spooferRoot) {
    throw new Error('Root elements not found');
  }

  try {
    // create translate function and get string bundle
    await getTrimBundle();

    const context = await getHomepageContext();

    // take CID from response and make metrics publisher
    makeMetricsPublisher(context.targetCustomerId);

    // if context response succeeds, replace initializationMetricsPublisher with one that can be tied to CID
    initializationMetricsPublisher = metricsPublisher.newChildActionPublisherForInitialization();

    // remove spinner once done loading and add the rest of the header elements
    toggleSpinner();
    // initialLogo can't publish metrics so remove and replace with one now that metricsPublisher is created
    logoRoot.removeChild(initialLogo);

    logoRoot.appendChild(renderLogo());
    navRoot.appendChild(renderNavigation(context));
    quickLinksRoot.appendChild(renderQuickLinks(context));

    const spooferBar = renderSpooferBar(context);

    if (spooferBar) {
      spooferRoot.appendChild(spooferBar);
    }

    if (getExperiment('BRAND_REGISTRY_SPOTLIGHT_906933') === 'T1') {
      const pzEligible = customerIsPzEligible(context);
      await appendSpotlight(spotlightRoot, pzEligible);
    }

    timerMetric.stop();
    metricsPublisher.publish(timerMetric);
    status = InitializationStatus.Success;
  } catch (error) {

    const finalError = errorOf(error);


    // Emit no initialization failure and log the error as a warning when it's a suspected client-side network error
    if (isNetworkError(finalError)) {
      networkErrorMetric.add(1);
      logWarning('A likely network error occurred during trim initialization', finalError);
    } else if (isUnsupportedBrowserError(finalError)) {
      unsupportedBrowserErrorMetric.add(1);
      logWarning('An error likely caused by use of an unsupported browser occurred during trim initialization',
        finalError);
    } else {
      status = InitializationStatus.Failure;
      logError('An error occurred during trim initialization', finalError);
    }

    toggleSpinner();
    root.insertAdjacentElement('afterend', renderError());
  }

  initializationMetric.setFailure(status === InitializationStatus.Failure);
  initializationMetricsPublisher.publish(initializationMetric);
  metricsPublisher.publish(networkErrorMetric);
  metricsPublisher.publish(unsupportedBrowserErrorMetric);
})();

export {};
